import React, { useState, useEffect } from "react";
import { formatDate } from "../utils/formatDate";
import { Form, Button, Modal, Spinner } from "react-bootstrap";
import {
  FaEdit,
  FaTrash,
  FaFileExport,
  FaArrowCircleRight,
  FaArrowCircleLeft,
  FaSearch,
  FaFilter,
  FaUserCog,
  FaLayerGroup,
} from "react-icons/fa";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "../Styles/AdminPages.css";
import formatName from "../utils/formatName";

function Users() {
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [allowedModules, setAllowedModules] = useState([]);
  const [bulkAllowedModules, setBulkAllowedModules] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const [intakes, setIntakes] = useState([]);
  const [selectedIntake, setSelectedIntake] = useState("");
  const [isIntakeLoading, setIsIntakeLoading] = useState(false);

  const [currentModulePage, setCurrentModulePage] = useState(1);
  const modulesPerPage = 5;

  const brandBlue = "#263F86";
  const softBlue = "rgba(95, 112, 245, 0.15)";
  const borderColor = "#EEF0F4";

  const indexOfLastModule = currentModulePage * modulesPerPage;
  const indexOfFirstModule = indexOfLastModule - modulesPerPage;
  const currentModules = modules.slice(indexOfFirstModule, indexOfLastModule);
  const totalModulePages = Math.ceil(modules.length / modulesPerPage);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API}/getUsers`);
        const data = await response.json();

        if (data.success) {
          setUsers(data.users || []);
        } else {
          setError(data.message || "Failed to fetch users.");
        }
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("An error occurred. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    const fetchModules = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API}/getAllModules`
        );
        const data = await response.json();

        if (data.success) {
          setModules(data.modules || []);
        } else {
          setError(data.message || "Failed to fetch modules.");
        }
      } catch (err) {
        console.error("Error fetching modules:", err);
        setError("An error occurred while fetching modules.");
      }
    };

    fetchUsers();
    fetchModules();
  }, []);

  useEffect(() => {
    setIsIntakeLoading(true);

    axios
      .get(`${process.env.REACT_APP_API}/getIntake`)
      .then((response) => {
        if (response.data.success && response.data.data) {
          const uniqueIntakes = [
            ...new Set(
              response.data.data
                .map((item) => item.intake)
                .filter((intake) => intake && intake.trim() !== "")
            ),
          ];
          setIntakes(uniqueIntakes);
        } else {
          setIntakes([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching intake data:", error);
        setIntakes([]);
      })
      .finally(() => setIsIntakeLoading(false));
  }, []);

  const filteredUsers = users.filter((user) => {
    const username = user.username || "";
    const intake = user.intake || "";

    const matchesSearch = username
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    const matchesIntake = selectedIntake === "" || intake === selectedIntake;

    return matchesSearch && matchesIntake;
  });

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setSelectedUsers([]);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/deleteUser/${userId}`,
        { method: "DELETE" }
      );

      const data = await response.json();

      if (data.success) {
        setUsers(users.filter((user) => user.user_id !== userId));
        setSelectedUsers((prev) => prev.filter((id) => id !== userId));

        toast.success("User deleted successfully!", {
          position: "top-right",
          autoClose: 2000,
        });
      } else {
        toast.error(data.message || "Failed to delete user.", {
          position: "top-right",
          autoClose: 2500,
        });
      }
    } catch (err) {
      console.error("Error during deletion:", err);
      toast.error("An error occurred. Please try again.", {
        position: "top-right",
        autoClose: 2500,
      });
    }
  };

  const handleOpenImportModal = () => {
    setSelectedFile(null);
    setShowImportModal(true);
  };

  const handleCloseImportModal = () => {
    setSelectedFile(null);
    setShowImportModal(false);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setButtonLoading(true);

    const loginUser = JSON.parse(localStorage.getItem("user"));

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("action_user_id", loginUser?.user_id || "");
    formData.append("action_username", loginUser?.username || "Unknown");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/excelImportLearner`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || "File imported successfully!", {
          position: "top-right",
          autoClose: 2500,
        });

        setSelectedFile(null);
        handleCloseImportModal();

        const refresh = await fetch(`${process.env.REACT_APP_API}/getUsers`);
        const refreshedData = await refresh.json();

        if (refreshedData.success) {
          setUsers(refreshedData.users || []);
        }
      } else {
        toast.error(response.data.message || "Failed to import file.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error.response?.data?.message || "Server Error!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setButtonLoading(false);
    }
  };

  const handleAllowModules = async (user) => {
    setSelectedUser(user);
    setAllowedModules([]);
    setCurrentModulePage(1);
    setShowModal(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/getUserAllowedModules?userId=${user.user_id}`
      );

      const data = await response.json();

      if (data.success) {
        setAllowedModules(data.data.map((module) => module.module_id));
      } else {
        toast.error(data.message || "Failed to fetch allowed modules.", {
          position: "top-right",
          autoClose: 2500,
        });
      }
    } catch (err) {
      console.error("Error fetching allowed modules:", err);
      toast.error("An error occurred. Please try again.", {
        position: "top-right",
        autoClose: 2500,
      });
    }
  };

  const handleCheckboxChange = (moduleId) => {
    setAllowedModules((prevModules) =>
      prevModules.includes(moduleId)
        ? prevModules.filter((id) => id !== moduleId)
        : [...prevModules, moduleId]
    );
  };

  const handleBulkCheckboxChange = (moduleId) => {
    setBulkAllowedModules((prevModules) =>
      prevModules.includes(moduleId)
        ? prevModules.filter((id) => id !== moduleId)
        : [...prevModules, moduleId]
    );
  };

  const handleSaveAllowedModules = async () => {
    if (!selectedUser) return;

    setButtonLoading(true);

    try {
      const loginUser = JSON.parse(localStorage.getItem("user"));

      const response = await fetch(`${process.env.REACT_APP_API}/allowModules`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: selectedUser.user_id,
          modules: allowedModules,
          action_user_id: loginUser?.user_id || null,
          action_username: loginUser?.username || "Unknown",
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success("Modules allowed successfully!", {
          position: "top-right",
          autoClose: 2000,
        });
        setShowModal(false);
      } else {
        toast.error(data.message || "Failed to allow modules.", {
          position: "top-right",
          autoClose: 2500,
        });
      }
    } catch (err) {
      console.error("Error allowing modules:", err);
      toast.error("An error occurred. Please try again.", {
        position: "top-right",
        autoClose: 2500,
      });
    } finally {
      setButtonLoading(false);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers((prevUsers) =>
      prevUsers.includes(userId)
        ? prevUsers.filter((id) => id !== userId)
        : [...prevUsers, userId]
    );
  };

  const handleSelectAllUsers = () => {
    const currentPageUserIds = currentUsers.map((user) => user.user_id);

    const allSelected =
      currentPageUserIds.length > 0 &&
      currentPageUserIds.every((id) => selectedUsers.includes(id));

    if (allSelected) {
      setSelectedUsers((prevUsers) =>
        prevUsers.filter((id) => !currentPageUserIds.includes(id))
      );
    } else {
      const mergedUsers = [...new Set([...selectedUsers, ...currentPageUserIds])];

      if (mergedUsers.length > 50) {
        toast.error("Please select maximum 50 users at a time.", {
          position: "top-right",
          autoClose: 2500,
        });
        return;
      }

      setSelectedUsers(mergedUsers);
    }
  };

  const handleOpenBulkModal = () => {
    if (selectedUsers.length === 0) {
      toast.error("Please select at least one user.", {
        position: "top-right",
        autoClose: 2500,
      });
      return;
    }

    if (selectedUsers.length > 50) {
      toast.error("Please select maximum 50 users at a time.", {
        position: "top-right",
        autoClose: 2500,
      });
      return;
    }

    setBulkAllowedModules([]);
    setCurrentModulePage(1);
    setShowBulkModal(true);
  };

  const handleSaveBulkModules = async () => {
    if (selectedUsers.length === 0) return;

    if (selectedUsers.length > 50) {
      toast.error("Please select maximum 50 users at a time.", {
        position: "top-right",
        autoClose: 2500,
      });
      return;
    }

    setButtonLoading(true);

    try {
      const loginUser = JSON.parse(localStorage.getItem("user"));

      const responses = await Promise.all(
        selectedUsers.map((userId) =>
          fetch(`${process.env.REACT_APP_API}/allowModules`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id: userId,
              modules: bulkAllowedModules,
              action_user_id: loginUser?.user_id || null,
              action_username: loginUser?.username || "Unknown",
            }),
          }).then((res) => res.json())
        )
      );

      const failed = responses.some((item) => !item.success);

      if (failed) {
        toast.error("Some users failed to update modules.", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      toast.success("Modules updated successfully!", {
        position: "top-right",
        autoClose: 2500,
      });

      setShowBulkModal(false);
      setSelectedUsers([]);
      setBulkAllowedModules([]);
    } catch (err) {
      console.error("Error allowing bulk modules:", err);

      toast.error("Failed to update modules.", {
        position: "top-right",
        autoClose: 2500,
      });
    } finally {
      setButtonLoading(false);
    }
  };

  const renderModulePagination = () => (
    <div className="d-flex justify-content-center align-items-center mt-3">
      <Button
        variant="light"
        size="sm"
        onClick={() => setCurrentModulePage((prev) => prev - 1)}
        disabled={currentModulePage === 1}
        className="me-2"
        style={{
          border: `1px solid ${borderColor}`,
          borderRadius: "10px",
        }}
      >
        <FaArrowCircleLeft color={brandBlue} size="18" />
      </Button>

      <span
        style={{
          fontSize: "13px",
          fontWeight: 700,
          color: "#475569",
        }}
      >
        Page {currentModulePage} of {totalModulePages || 1}
      </span>

      <Button
        variant="light"
        size="sm"
        onClick={() => setCurrentModulePage((prev) => prev + 1)}
        disabled={currentModulePage === totalModulePages}
        className="ms-2"
        style={{
          border: `1px solid ${borderColor}`,
          borderRadius: "10px",
        }}
      >
        <FaArrowCircleRight color={brandBlue} size="18" />
      </Button>
    </div>
  );

  const currentPageUserIds = currentUsers.map((user) => user.user_id);

  const isAllCurrentPageSelected =
    currentPageUserIds.length > 0 &&
    currentPageUserIds.every((id) => selectedUsers.includes(id));

  return (
    <section className="admin-page" aria-labelledby="users-page-title">
      <div className="admin-page__header">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <div style={{ textAlign: "left" }}>
              <p className="admin-page__eyebrow">USER MANAGEMENT</p>
              <h1 id="users-page-title">Users</h1>
              <p className="admin-page__intro">
                Manage learners, intake groups, account roles, and module
                permissions.
              </p>
            </div>
          </div>

          <Button
            onClick={handleOpenImportModal}
            disabled={loading}
            style={{
              background: brandBlue,
              color: "#ffffff",
              border: "none",
              borderRadius: "12px",
              padding: "10px 16px",
              fontWeight: 700,
              boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
            }}
          >
            <FaFileExport size={13} className="me-2" />
            Import Excel
          </Button>
        </div>
      </div>

      <div
        className="mb-4"
        style={{
          background: "#ffffff",
          borderRadius: "18px",
          padding: "18px 20px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
          border: `1px solid ${borderColor}`,
        }}
      >
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
          <div className="d-flex align-items-center gap-2">
            <FaFilter color={brandBlue} />
            <h6 className="fw-bold mb-0">Filter Users</h6>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-12 col-md-8">
            <div style={{ position: "relative" }}>
              <FaSearch
                size={14}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "14px",
                  transform: "translateY(-50%)",
                  color: "#5F6B7A",
                }}
              />

              <Form.Control
                type="text"
                placeholder="Search by name"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                  setSelectedUsers([]);
                }}
                style={{
                  borderRadius: "12px",
                  padding: "11px 14px 11px 38px",
                  border: `1px solid ${borderColor}`,
                  fontSize: "14px",
                }}
              />
            </div>
          </div>

          <div className="col-12 col-md-4">
            <select
              className="form-select"
              value={selectedIntake}
              onChange={(e) => {
                setSelectedIntake(e.target.value);
                setCurrentPage(1);
                setSelectedUsers([]);
              }}
              disabled={isIntakeLoading}
              style={{
                borderRadius: "12px",
                padding: "11px 14px",
                border: `1px solid ${borderColor}`,
                fontSize: "14px",
              }}
            >
              <option value="">All Intakes</option>
              {intakes.map((intake, index) => (
                <option key={index} value={intake}>
                  {intake}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div
        style={{
          background: "#ffffff",
          borderRadius: "18px",
          padding: "20px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
          border: `1px solid ${borderColor}`,
        }}
      >
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
          <div className="d-flex align-items-center gap-2">
            <FaUserCog color={brandBlue} />
            <h6 className="fw-bold mb-0">User List</h6>
          </div>

          <Button
            onClick={handleOpenBulkModal}
            disabled={selectedUsers.length === 0}
            style={{
              background: selectedUsers.length === 0 ? "#64748B" : brandBlue,
              color: "#fff",
              border: "none",
              borderRadius: "12px",
              padding: "9px 16px",
              fontWeight: 700,
              boxShadow:
                selectedUsers.length === 0
                  ? "none"
                  : "0 6px 18px rgba(0,0,0,0.12)",
            }}
          >
            <FaLayerGroup className="me-2" size={13} />
            Allow Modules ({selectedUsers.length})
          </Button>
        </div>

        {loading ? (
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ height: "50vh" }}
          >
            <Spinner animation="border" style={{ color: brandBlue }} />
            <p className="mt-3 mb-0">Loading users...</p>
          </div>
        ) : error ? (
          <div
            className="text-center"
            style={{
              padding: "36px",
              color: "#DC2626",
              fontWeight: 700,
            }}
          >
            {error}
          </div>
        ) : filteredUsers.length > 0 ? (
          <>
            <div style={{ width: "100%" }}>
              <table
                className="table mb-0"
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                  tableLayout: "fixed",
                  fontSize: "12.5px",
                }}
              >
                <colgroup>
                  <col style={{ width: "4%" }} />
                  <col style={{ width: "5%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "23%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "8%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={thStyle}>
                      <Form.Check
                        type="checkbox"
                        checked={isAllCurrentPageSelected}
                        onChange={handleSelectAllUsers}
                      />
                    </th>
                    <th style={thStyle}>No</th>
                    <th style={thStyleLeft}>Username</th>
                    <th style={thStyleLeft}>Email</th>
                    <th style={thStyle}>NRIC</th>
                    <th style={thStyle}>Role</th>
                    <th style={thStyle}>Intake</th>
                    <th style={thStyle}>Created At</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {currentUsers.map((user, index) => (
                    <tr
                      key={user.user_id}
                      style={{
                        borderBottom: `1px solid ${borderColor}`,
                      }}
                    >
                      <td style={tdStyle}>
                        <Form.Check
                          type="checkbox"
                          checked={selectedUsers.includes(user.user_id)}
                          onChange={() => handleSelectUser(user.user_id)}
                        />
                      </td>

                      <td style={tdStyle}>{indexOfFirstUser + index + 1}</td>

                      <td style={tdStyleLeft}>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: "28px",
                              height: "28px",
                              borderRadius: "9px",
                              background: softBlue,
                              color: brandBlue,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "11.5px",
                              flexShrink: 0,
                            }}
                          >
                            {(user.username || "U").charAt(0).toUpperCase()}
                          </div>

                          <span
                            style={{
                              color: "#1F2937",
                              wordBreak: "break-word",
                            }}
                          >
                            {formatName(user.username)}
                          </span>
                        </div>
                      </td>

                      <td style={tdStyleLeft}>{user.email || "-"}</td>
                      <td style={tdStyle}>{user.NRIC || "-"}</td>

                      <td style={tdStyle}>
                        <span
                          style={{
                            padding: "5px 9px",
                            borderRadius: "999px",
                            background:
                              user.user_role === "admin"
                                ? "#E0E7FF"
                                : "#ECFDF5",
                            color:
                              user.user_role === "admin"
                                ? "#3730A3"
                                : "#047857",
                            fontSize: "11px",
                            textTransform: "capitalize",
                          }}
                        >
                          {user.user_role || "-"}
                        </span>
                      </td>

                      <td style={tdStyle}>{user.intake || "-"}</td>
                      <td style={tdStyle}>{formatDate(user.created_at)}</td>

                      <td style={tdStyle}>
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            className="btn"
                            onClick={() => handleAllowModules(user)}
                            title="Allow Modules"
                            style={{
                              background: "#F8FAFC",
                              color: brandBlue,
                              border: `1px solid ${borderColor}`,
                              borderRadius: "9px",
                              padding: "7px 10px",
                              fontWeight: 800,
                              fontSize: "12px",
                            }}
                          >
                            <FaEdit size={12} />
                          </button>

                          <button
                            className="btn"
                            onClick={() => handleDelete(user.user_id)}
                            title="Delete User"
                            style={{
                              background: "#FEF2F2",
                              color: "#DC2626",
                              border: "1px solid #FECACA",
                              borderRadius: "9px",
                              padding: "7px 10px",
                              fontWeight: 800,
                              fontSize: "12px",
                            }}
                          >
                            <FaTrash size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <nav aria-label="Page navigation" className="mt-4">
                <ul className="pagination justify-content-center flex-wrap gap-1">
                  <li
                    className={`page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      style={{ borderRadius: "10px" }}
                    >
                      Previous
                    </button>
                  </li>

                  {currentPage > 3 && (
                    <>
                      <li className="page-item">
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(1)}
                          style={{ borderRadius: "10px" }}
                        >
                          1
                        </button>
                      </li>

                      {currentPage > 4 && (
                        <li className="page-item disabled">
                          <span
                            className="page-link"
                            style={{ borderRadius: "10px" }}
                          >
                            ...
                          </span>
                        </li>
                      )}
                    </>
                  )}

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(
                      (page) =>
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 2 &&
                          page <= currentPage + 2)
                    )
                    .map((page) => (
                      <li
                        key={page}
                        className={`page-item ${
                          page === currentPage ? "active" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(page)}
                          style={{
                            borderRadius: "10px",
                            background:
                              page === currentPage ? brandBlue : "#fff",
                            borderColor:
                              page === currentPage ? brandBlue : borderColor,
                          }}
                        >
                          {page}
                        </button>
                      </li>
                    ))}

                  {currentPage < totalPages - 2 && (
                    <>
                      {currentPage < totalPages - 3 && (
                        <li className="page-item disabled">
                          <span
                            className="page-link"
                            style={{ borderRadius: "10px" }}
                          >
                            ...
                          </span>
                        </li>
                      )}

                      <li className="page-item">
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(totalPages)}
                          style={{ borderRadius: "10px" }}
                        >
                          {totalPages}
                        </button>
                      </li>
                    </>
                  )}

                  <li
                    className={`page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      style={{ borderRadius: "10px" }}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </>
        ) : (
          <div
            className="text-center"
            style={{
              padding: "36px",
              color: "#5F6B7A",
              fontWeight: 600,
            }}
          >
            No users found.
          </div>
        )}
      </div>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
        style={{ fontFamily: "Manrope-Regular" }}
      >
        <Modal.Header
          closeButton
          style={{
            borderBottom: `1px solid ${borderColor}`,
            padding: "20px 24px",
          }}
        >
          <Modal.Title
            style={{
              color: brandBlue,
              fontWeight: 800,
              fontSize: "20px",
            }}
          >
            Allowed Modules
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "24px" }}>
          {selectedUser && (
            <>
              <div
                className="mb-4"
                style={{
                  background: "#F8FAFC",
                  border: `1px solid ${borderColor}`,
                  borderRadius: "16px",
                  padding: "16px",
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "14px",
                      background: softBlue,
                      color: brandBlue,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                    }}
                  >
                    {(selectedUser.username || "U").charAt(0).toUpperCase()}
                  </div>

                  <div>
                    <div
                      style={{
                        fontWeight: 800,
                        color: "#1F2937",
                      }}
                    >
                      {formatName(selectedUser.username)}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#64748B",
                      }}
                    >
                      {selectedUser.email}
                    </div>
                  </div>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold d-flex align-items-center gap-2">
                  <FaLayerGroup color={brandBlue} />
                  Modules
                </Form.Label>

                <div
                  style={{
                    border: `1px solid ${borderColor}`,
                    borderRadius: "14px",
                    overflow: "hidden",
                  }}
                >
                  {currentModules.map((module, index) => (
                    <label
                      key={module.module_id}
                      className="d-flex align-items-center justify-content-between"
                      style={{
                        padding: "13px 15px",
                        borderBottom:
                          index === currentModules.length - 1
                            ? "none"
                            : `1px solid ${borderColor}`,
                        cursor: "pointer",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          color: "#334155",
                        }}
                      >
                        {module.module_name}
                      </span>

                      <Form.Check
                        type="checkbox"
                        checked={allowedModules.includes(module.module_id)}
                        onChange={() => handleCheckboxChange(module.module_id)}
                      />
                    </label>
                  ))}
                </div>

                {modules.length > modulesPerPage && renderModulePagination()}
              </Form.Group>
            </>
          )}
        </Modal.Body>

        <Modal.Footer
          style={{
            borderTop: `1px solid ${borderColor}`,
            padding: "18px 24px",
          }}
        >
          <Button
            variant="light"
            onClick={() => setShowModal(false)}
            disabled={buttonLoading}
            style={{
              borderRadius: "10px",
              padding: "9px 16px",
              fontWeight: 700,
              border: `1px solid ${borderColor}`,
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSaveAllowedModules}
            disabled={buttonLoading}
            style={{
              backgroundColor: brandBlue,
              color: "#ffffff",
              border: "none",
              borderRadius: "10px",
              padding: "9px 18px",
              fontWeight: 700,
            }}
          >
            {buttonLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showBulkModal}
        onHide={() => setShowBulkModal(false)}
        centered
        size="lg"
        style={{ fontFamily: "Manrope-Regular" }}
      >
        <Modal.Header
          closeButton
          style={{
            borderBottom: `1px solid ${borderColor}`,
            padding: "20px 24px",
          }}
        >
          <Modal.Title
            style={{
              color: brandBlue,
              fontWeight: 800,
              fontSize: "20px",
            }}
          >
            Allow Modules for Selected Users
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "24px" }}>
          <div
            className="mb-4"
            style={{
              background: "#F8FAFC",
              border: `1px solid ${borderColor}`,
              borderRadius: "16px",
              padding: "16px",
            }}
          >
            <div
              style={{
                fontWeight: 800,
                color: "#1F2937",
                marginBottom: "4px",
              }}
            >
              Selected Users
            </div>

            <div
              style={{
                fontSize: "13px",
                color: "#64748B",
              }}
            >
              {selectedUsers.length} user{selectedUsers.length > 1 ? "s" : ""}{" "}
              selected. Maximum allowed is 50 users at a time.
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold d-flex align-items-center gap-2">
              <FaLayerGroup color={brandBlue} />
              Modules
            </Form.Label>

            <div
              style={{
                border: `1px solid ${borderColor}`,
                borderRadius: "14px",
                overflow: "hidden",
              }}
            >
              {currentModules.map((module, index) => (
                <label
                  key={module.module_id}
                  className="d-flex align-items-center justify-content-between"
                  style={{
                    padding: "13px 15px",
                    borderBottom:
                      index === currentModules.length - 1
                        ? "none"
                        : `1px solid ${borderColor}`,
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      color: "#334155",
                    }}
                  >
                    {module.module_name}
                  </span>

                  <Form.Check
                    type="checkbox"
                    checked={bulkAllowedModules.includes(module.module_id)}
                    onChange={() => handleBulkCheckboxChange(module.module_id)}
                  />
                </label>
              ))}
            </div>

            {modules.length > modulesPerPage && renderModulePagination()}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer
          style={{
            borderTop: `1px solid ${borderColor}`,
            padding: "18px 24px",
          }}
        >
          <Button
            variant="light"
            onClick={() => setShowBulkModal(false)}
            disabled={buttonLoading}
            style={{
              borderRadius: "10px",
              padding: "9px 16px",
              fontWeight: 700,
              border: `1px solid ${borderColor}`,
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSaveBulkModules}
            disabled={buttonLoading}
            style={{
              backgroundColor: brandBlue,
              color: "#ffffff",
              border: "none",
              borderRadius: "10px",
              padding: "9px 18px",
              fontWeight: 700,
            }}
          >
            {buttonLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showImportModal}
        onHide={handleCloseImportModal}
        size="lg"
        centered
        style={{ fontFamily: "Manrope-Regular" }}
      >
        <Modal.Header
          closeButton
          style={{
            borderBottom: `1px solid ${borderColor}`,
            padding: "20px 24px",
          }}
        >
          <Modal.Title
            style={{
              color: brandBlue,
              fontWeight: 800,
              fontSize: "20px",
            }}
          >
            Import Excel File
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "24px" }}>
          <Form.Group controlId="formFile" className="mb-3">
            <Form.Label className="fw-bold">
              Upload learner Excel file
            </Form.Label>
            <Form.Control
              type="file"
              accept=".xlsx, .xls"
              onChange={(event) => setSelectedFile(event.target.files[0])}
              style={{
                borderRadius: "12px",
                padding: "11px 14px",
                border: `1px solid ${borderColor}`,
              }}
            />
            <small style={{ color: "#64748B" }}>
              Supported formats: .xlsx and .xls
            </small>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer
          style={{
            borderTop: `1px solid ${borderColor}`,
            padding: "18px 24px",
          }}
        >
          <Button
            variant="light"
            onClick={handleCloseImportModal}
            disabled={buttonLoading}
            style={{
              borderRadius: "10px",
              padding: "9px 16px",
              fontWeight: 700,
              border: `1px solid ${borderColor}`,
            }}
          >
            Close
          </Button>

          <Button
            onClick={handleFileUpload}
            disabled={buttonLoading}
            style={{
              background: brandBlue,
              color: "#ffffff",
              border: "none",
              borderRadius: "10px",
              padding: "9px 18px",
              fontWeight: 700,
            }}
          >
            {buttonLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Importing...
              </>
            ) : (
              "Import File"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />
    </section>
  );
}

const thStyle = {
  textAlign: "center",
  background: "#F8FAFC",
  color: "#475569",
  fontSize: "11px",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  borderBottom: "1px solid #EEF0F4",
  padding: "11px 12px",
  wordBreak: "break-word",
};

const thStyleLeft = {
  ...thStyle,
  textAlign: "left",
};

const tdStyle = {
  textAlign: "center",
  padding: "10px 12px",
  color: "#334155",
  fontWeight: 600,
  fontSize: "12.5px",
  verticalAlign: "middle",
  letterSpacing: "0.3px",
  wordBreak: "break-word",
};

const tdStyleLeft = {
  ...tdStyle,
  textAlign: "left",
};

export default Users;
